/**
 * CheckoutReturnPage — Post-payment landing for Stripe Elements checkout.
 *
 * Stripe navigates here (via return_url) after payment confirmation.
 *
 * Authenticated flow: calls POST /v2/billing/sync, invalidates query cache,
 * then redirects to /dashboard.
 *
 * Guest flow (no token): calls POST /v2/billing/guest-provision to create the
 * Guardrail account and send a magic-link email. Shows "check your email"
 * instead of redirecting (the user has no active session yet).
 *
 * Routes:
 *  /checkout/return?subscription_id=sub_xxx  — Elements flow (new)
 *  /checkout/return?session_id=cs_xxx        — Legacy embedded checkout
 *
 * @param subscription_id - Stripe subscription ID (new Elements flow)
 * @param session_id      - Stripe Checkout Session ID (legacy, backward compat)
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { TrafficLightLogo } from '@/components/TrafficLightLogo';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

type PageState = 'syncing' | 'success' | 'cancelled' | 'error' | 'check_email';

/** Max ms to wait for billing sync before navigating anyway. */
const SYNC_TIMEOUT_MS = 8_000;
/** Delay after successful sync before redirecting. */
const REDIRECT_DELAY_MS = 2_000;

export default function CheckoutReturnPage() {
  useDocumentTitle('Checkout');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token, loading: authLoading, setTokens } = useAuth();

  // Derive checkout ID before state init so useState can use it as initial value.
  const subscriptionId = searchParams.get('subscription_id');
  const sessionId = searchParams.get('session_id');
  const checkoutId = subscriptionId ?? sessionId;
  const idType: 'subscriptionId' | 'sessionId' = subscriptionId ? 'subscriptionId' : 'sessionId';
  // Optional org name forwarded from the guest checkout form via return URL param.
  const orgName = searchParams.get('org_name') || undefined;

  // Derive initial state from URL params — avoids setState calls inside the
  // effect for the missing-checkoutId case (react-hooks/no-direct-set-state-in-effect).
  const [state, setState] = useState<PageState>(() =>
    checkoutId ? 'syncing' : 'error'
  );
  const [message, setMessage] = useState<string>(() =>
    checkoutId
      ? ''
      : 'No checkout ID provided. If payment was taken, contact support.'
  );
  const [guestEmail, setGuestEmail] = useState('');
  const syncCalledRef = useRef(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!checkoutId) return;

    // Wait for auth to finish loading before deciding authenticated vs guest path.
    if (authLoading) return;

    if (syncCalledRef.current) return;
    syncCalledRef.current = true;

    const timeoutId = setTimeout(() => {
      // Sync timed out — navigate anyway; webhook will eventually reconcile.
      redirectTimerRef.current = setTimeout(() => navigate('/dashboard', { replace: true }), 1_000);
    }, SYNC_TIMEOUT_MS);

    const doSync = async () => {
      try {
        if (token) {
          // Authenticated: sync entitlements then go to dashboard.
          await apiClient.syncSubscription(token);
          // Poll up to 3× (1s apart) to confirm the tier has been promoted from
          // pending_tier before redirecting. This prevents the dashboard from
          // showing a stale "Free" tier right after a paid checkout completes.
          let pollAttempts = 0;
          const maxPolls = 3;
          while (pollAttempts < maxPolls) {
            await queryClient.invalidateQueries({ queryKey: ['userInfo', 'me'] });
            try {
              const freshData = await apiClient.getMe(token);
              if (
                freshData?.subscription?.tier &&
                freshData.subscription.tier !== 'free' &&
                freshData.subscription.tier !== 'trial'
              ) {
                break; // Tier has been updated — no need to keep polling
              }
            } catch {
              // Non-fatal — proceed after max polls
            }
            await new Promise<void>((r) => setTimeout(r, 1000));
            pollAttempts++;
          }
          // Final invalidation so the dashboard renders the freshest data.
          await queryClient.invalidateQueries({ queryKey: ['userInfo', 'me'] });
          clearTimeout(timeoutId);
          setState('success');
          setMessage('Your subscription is active. Welcome to Code!');
          redirectTimerRef.current = setTimeout(
            () => navigate('/dashboard', { replace: true }),
            REDIRECT_DELAY_MS
          );
        } else {
          // Guest: provision account; auto-login if tokens returned, else show "check email".
          clearTimeout(timeoutId);
          try {
            const result = await apiClient.guestProvision(checkoutId, idType, orgName);
            if (result.email) setGuestEmail(result.email);

            // Auto-login if the backend returned tokens
            if (result.access_token) {
              setTokens(result.access_token, result.refresh_token);
              await queryClient.invalidateQueries({ queryKey: ['userInfo', 'me'] });
              setState('success');
              setMessage('Your subscription is active. Welcome to Code!');
              redirectTimerRef.current = setTimeout(
                () => navigate('/dashboard', { replace: true }),
                REDIRECT_DELAY_MS
              );
              return;
            }
          } catch {
            // Non-fatal — webhook will reconcile; user can always request magic link
          }
          setState('check_email');
          setMessage('We sent a sign-in link to your email.');
        }
      } catch {
        // Non-fatal — webhook will reconcile in the background.
        clearTimeout(timeoutId);
        setState('success');
        setMessage(
          token ? 'Your subscription is active. Welcome to Code!' : 'Payment received.'
        );
        if (token) {
          redirectTimerRef.current = setTimeout(
            () => navigate('/dashboard', { replace: true }),
            REDIRECT_DELAY_MS
          );
        }
      }
    };

    doSync();

    return () => {
      clearTimeout(timeoutId);
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, [checkoutId, idType, authLoading, token, navigate, queryClient]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const stateConfig: Record<PageState, { icon: React.ReactNode; title: string; color: string }> = {
    syncing: {
      icon: <Loader2 className="w-10 h-10 text-emerald-400 animate-spin" />,
      title: 'Activating your plan…',
      color: 'text-zinc-300',
    },
    success: {
      icon: <CheckCircle2 className="w-10 h-10 text-emerald-400" />,
      title: 'Payment successful',
      color: 'text-emerald-400',
    },
    check_email: {
      icon: <Mail className="w-10 h-10 text-sky-400" />,
      title: 'Check your email',
      color: 'text-sky-400',
    },
    cancelled: {
      icon: <XCircle className="w-10 h-10 text-zinc-400" />,
      title: 'Checkout cancelled',
      color: 'text-zinc-300',
    },
    error: {
      icon: <XCircle className="w-10 h-10 text-red-400" />,
      title: 'Something went wrong',
      color: 'text-red-400',
    },
  };

  const config = stateConfig[state];

  return (
    <div className="min-h-screen bg-[rgb(5,5,5)] flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-zinc-800/80 border-zinc-700">
        <CardContent className="py-12 text-center space-y-5">
          {/* Branding */}
          <div className="flex justify-center mb-6">
            <TrafficLightLogo size="lg" />
          </div>

          <div className="flex justify-center">{config.icon}</div>

          <div className="space-y-1.5">
            <h1 className={`text-xl font-bold ${config.color}`}>{config.title}</h1>
            {message && <p className="text-zinc-400 text-sm">{message}</p>}

            {state === 'check_email' && (
              <div className="space-y-3 pt-2">
                {guestEmail && (
                  <p className="text-zinc-500 text-xs">
                    Sent to: <span className="text-zinc-300">{guestEmail}</span>
                  </p>
                )}
                <p className="text-zinc-500 text-xs">
                  Click the link in the email to sign in and access your dashboard.
                </p>
                <a
                  href="/login"
                  className="inline-flex items-center gap-2 rounded-lg px-5 py-2 bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors"
                >
                  Go to sign in
                </a>
              </div>
            )}

            {state === 'success' && (
              <p className="text-zinc-500 text-xs">Redirecting to your dashboard…</p>
            )}
          </div>

          {(state === 'cancelled' || state === 'error') && (
            <button
              onClick={() => navigate('/pricing')}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
            >
              Back to pricing
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
